import { useCallback, useState } from "react";
type UploadStatus = "idle" | "uploading" | "success" | "error";
type FileUploadState = {
  status: UploadStatus;
  progress: number;
  error?: string;
};
type UseFileUploadOptions = {
  onSuccess?: (file: File, response: any) => void;
  onError?: (file: File, error: Error) => void;
  onProgress?: (file: File, progress: number) => void;
  resizeImage?: {
    maxDimension: number;
    quality: number;
  };
};

// resize image using canvas API
async function resizeImage(file: File, opts = {
  maxDimension: 1000,
  quality: 0.7
}): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("Could not get canvas context"));
      return;
    }
    img.onload = () => {
      const {
        width,
        height
      } = img;
      const largestDimension = width > height ? "width" : "height";
      const initialValueOfLargestDimension = img[largestDimension as keyof HTMLImageElement] as number;
      const targetValueOfLargestDimension = initialValueOfLargestDimension > opts.maxDimension ? opts.maxDimension : initialValueOfLargestDimension;

      // calculate new dimensions maintaining aspect ratio
      const aspectRatio = width / height;
      let newWidth, newHeight;
      if (largestDimension === "width") {
        newWidth = targetValueOfLargestDimension;
        newHeight = targetValueOfLargestDimension / aspectRatio;
      } else {
        newHeight = targetValueOfLargestDimension;
        newWidth = targetValueOfLargestDimension * aspectRatio;
      }

      // set canvas dimensions
      canvas.width = newWidth;
      canvas.height = newHeight;

      // draw resized image
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      // convert to blob with proper error handling
      canvas.toBlob(blob => {
        if (blob) {
          // ensure blob has proper size
          if (blob.size === 0) {
            reject(new Error("Resized image blob is empty"));
            return;
          }

          // create new file with resized image
          const resizedFile = new File([blob], file.name, {
            type: file.type,
            // preserve original type instead of forcing JPEG
            lastModified: Date.now()
          });

          // verify the file was created properly
          if (resizedFile.size === 0) {
            reject(new Error("Resized file is empty"));
            return;
          }
          resolve(resizedFile);
        } else {
          reject(new Error("Failed to create resized image"));
        }
      }, file.type,
      // preserve original type instead of forcing JPEG
      opts.quality);
    };
    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };
    img.src = URL.createObjectURL(file);
  });
}
export function useFileUpload(options: UseFileUploadOptions = {}) {
  const [uploadStates, setUploadStates] = useState<Map<string, FileUploadState>>(new Map());
  const uploadFile = useCallback(async (file: File, uploadUrl: string, contentType?: string) => {
    const fileId = Math.random().toString(36).substr(2, 9);

    // initialize upload state
    setUploadStates(prev => new Map(prev).set(fileId, {
      status: "uploading",
      progress: 0
    }));
    try {
      let fileToUpload = file;

      // resize image if it's an image file and resize options are provided
      if (options.resizeImage && file.type.startsWith("image/") && file.type !== "image/svg+xml" // don't resize SVGs
      ) {
        try {
          fileToUpload = await resizeImage(file, options.resizeImage);
        } catch (resizeError) {
          console.warn("Failed to resize image, uploading original:", resizeError);
          // continue with original file if resize fails
        }
      }

      // ensure we have a valid file to upload
      if (!fileToUpload || fileToUpload.size === 0) {
        throw new Error("File is empty or invalid");
      }

      // ensure we have a valid content type
      const finalContentType = contentType || fileToUpload.type || "application/octet-stream";
      const response = await fetch(uploadUrl, {
        method: "PUT",
        body: fileToUpload,
        headers: {
          "Content-Type": finalContentType
        }
      });
      if (!response.ok) {
        throw new Error(`File upload request failed with status ${response.status}`);
      }

      // update state to success
      setUploadStates(prev_1 => {
        const newMap_0 = new Map(prev_1);
        newMap_0.set(fileId, {
          status: "success",
          progress: 100
        });
        return newMap_0;
      });

      // create response object
      const uploadResponse = {
        success: true,
        status: response.status,
        fileId,
        fileName: fileToUpload.name,
        fileSize: fileToUpload.size,
        uploadedAt: new Date().toISOString(),
        originalFileSize: file.size,
        wasResized: fileToUpload !== file,
        key: uploadUrl
      };
      options.onSuccess?.(file, uploadResponse);
      return uploadResponse;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Upload failed";
      setUploadStates(prev_0 => {
        const newMap = new Map(prev_0);
        newMap.set(fileId, {
          status: "error",
          progress: 0,
          error: errorMessage
        });
        return newMap;
      });
      options.onError?.(file, error instanceof Error ? error : new Error(errorMessage));
      throw error;
    }
  }, [options]);
  const resetUploadState = useCallback((fileId_0: string) => {
    setUploadStates(prev_2 => {
      const newMap_1 = new Map(prev_2);
      newMap_1.delete(fileId_0);
      return newMap_1;
    });
  }, []);
  const getUploadState = useCallback((fileId_1: string) => {
    return uploadStates.get(fileId_1);
  }, [uploadStates]);
  return {
    uploadFile,
    resetUploadState,
    getUploadState,
    uploadStates: Array.from(uploadStates.entries())
  };
}